// Form rules shared by every screen: client-side checks that mirror the contract's limits (the
// server re-validates), field errors from the API's `details`, and the typed-name confirmation
// the wireframes require before a destructive action.
import type { ApiError } from "../api/errors";
import { parseUsd } from "./money";

/** Field name → message. A nested field is dotted: `/rates/0/markupBp` → `rates.0.markupBp`. */
export type FieldErrors = Record<string, string>;

/** A rule answers the problem with the value, or null when it is acceptable. */
export type Rule = (value: string) => string | null;

export function hasErrors(errors: FieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

function fieldName(pointer: string): string {
  return pointer
    .replace(/^\//, "")
    .split("/")
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .join(".");
}

/** Field-level messages from an API error's details; the first message per field wins. */
export function fieldErrorsFrom(error: ApiError): FieldErrors {
  const errors: FieldErrors = {};
  for (const detail of error.details) {
    if (!detail.field) continue;
    const name = fieldName(detail.field);
    errors[name] ??= detail.message ?? detail.code;
  }
  return errors;
}

/**
 * Every detail of an API error as one line for display: `field: reason` when the detail names a
 * field (the API names the field and why, never the value), else just the reason (or its code).
 */
export function errorDetailLines(error: ApiError): string[] {
  return error.details.map((detail) => {
    const text = detail.message ?? detail.code;
    return detail.field ? `${fieldName(detail.field)}: ${text}` : text;
  });
}

/** Run each field's rules in order; a field reports only its first failure. */
export function validate<F extends string>(values: Record<F, string>, rules: Partial<Record<F, readonly Rule[]>>): FieldErrors {
  const errors: FieldErrors = {};
  for (const [field, fieldRules] of Object.entries(rules) as [F, readonly Rule[]][]) {
    for (const rule of fieldRules) {
      const problem = rule(values[field]);
      if (problem !== null) {
        errors[field] = problem;
        break;
      }
    }
  }
  return errors;
}

export const required: Rule = (value) => (value.trim() === "" ? "Required." : null);

export function lengthBetween(min: number, max: number): Rule {
  return (value) => {
    const length = [...value.trim()].length;
    return length < min || length > max ? `Between ${min} and ${max} characters.` : null;
  };
}

/** A forced close or finalize needs a 10–500 character reason (SCR-006, SCR-012). */
export const forceReason: Rule = lengthBetween(10, 500);

export const month: Rule = (value) =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(value.trim()) ? null : "A month as YYYY-MM.";

export function integerBetween(min: number, max: number): Rule {
  return (value) => {
    const text = value.trim();
    if (!/^-?\d+$/.test(text)) return "A whole number.";
    const number = Number(text);
    return number < min || number > max ? `Between ${min} and ${max}.` : null;
  };
}

export const usdAmount: Rule = (value) => (parseUsd(value) === null ? "A dollar amount with at most six decimals." : null);

/** Destructive actions confirm with the object's name typed exactly (SCR-004). */
export function typedNameMatches(expected: string, typed: string): boolean {
  return expected !== "" && typed.trim() === expected;
}
